import React, { useCallback, useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import type { TrackType } from '../../../api/cc-smarts/models/TrackType';
import type { ContentDetails } from '../../../types/activeContent';

import { PlaylistItemContextualMenu } from './PlaylistItemContextualMenu';
import { PlaylistItemIcon } from './PlaylistItemIcon';

export interface PlaylistItemData {
	id: string;
	title: string;
	trackType: TrackType;
	author?: string;
	avatarUrl?: string;
	spaceKey?: string;
	contentType?: string; //page, embed, whiteboard, folder
	isLive: boolean;
	emojiId?: string;
	contentId: string;
	supportingContent: ContentDetails[];
	mainContent: ContentDetails;
}

const messages = defineMessages({
	unknownAuthor: {
		id: 'playlist.item.unknown-author',
		defaultMessage: 'Unknown author',
		description: 'Fallback author name when a playlist item has no author',
	},
	typeBriefing: {
		id: 'playlist.item.type.briefing',
		defaultMessage: 'Briefing',
		description: 'Label for briefing type playlist items',
	},
	typeNarration: {
		id: 'playlist.item.type.narration',
		defaultMessage: 'Narration',
		description: 'Label for narration type playlist items',
	},
});

interface PlaylistItemProps {
	data: PlaylistItemData;
	isFirstItem?: boolean;
	isLastItem?: boolean;
	onItemClick?: (item: PlaylistItemData) => void;
	currentIndex: number;
	allItems: PlaylistItemData[];
}

const styles = cssMap({
	gridContainer: {
		display: 'grid',
		position: 'relative',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
	},
	container: {
		gridColumn: '1 / -1',
		gridRow: '1',
		width: '100%',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.medium'),
	},
	content: {
		flex: 1,
		textAlign: 'left',
	},
	contentWithMenu: {
		paddingRight: token('space.500'),
	},
	iconWrapper: {
		display: 'flex',
		alignSelf: 'flex-start',
		paddingTop: token('space.050'),
		flexShrink: 0,
		width: '24px',
		justifyContent: 'center',
	},
	title: {
		height: '20px',
	},
	subtitle: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		height: '20px',
	},
	authorName: {
		flexShrink: 1,
		minWidth: '0px',
	},
	separator: {
		flexShrink: 0,
	},
	type: {
		flexShrink: 0,
	},
	contextualMenuOverlay: {
		gridColumn: '1 / -1',
		gridRow: '1',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		paddingRight: token('space.100'),
		pointerEvents: 'none',
	},
	contextualMenuButton: {
		opacity: 0,
		transition: 'opacity 0.2s ease',
		pointerEvents: 'auto',
	},
	contextualMenuButtonVisible: {
		opacity: 1,
	},
});

export const PlaylistItem = ({
	data,
	onItemClick,
	isFirstItem,
	isLastItem,
	currentIndex,
	allItems,
}: PlaylistItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { title, trackType, author } = data;

	const { sendUIEvent } = usePlatformInstrumentation();

	const displayTitle = title || '';
	const displayAuthor = author || formatMessage(messages.unknownAuthor);
	const audioType =
		trackType === 'BRIEFING'
			? formatMessage(messages.typeBriefing)
			: formatMessage(messages.typeNarration);

	const titleRef = useRef<HTMLElement>(null);
	const authorRef = useRef<HTMLElement>(null);
	const [isTitleTruncated, setIsTitleTruncated] = useState(false);
	const [isAuthorTruncated, setIsAuthorTruncated] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	// isMenuOpen is needed to still show the menu trigger button when the user is interacting with the menu
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const shouldShowMenuTrigger = isHovered || isMenuOpen;

	const handleClick = useCallback(() => {
		sendUIEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'playlistItem',
			actionSubjectId: 'playlist',
			source: 'playlist',
			attributes: {
				contentType: data.contentType,
				trackType,
				itemPosition: currentIndex,
				totalPlaylistSize: allItems?.length,
			},
		});
		onItemClick?.(data);
	}, [onItemClick, data, allItems?.length, currentIndex, sendUIEvent, trackType]);

	useEffect(() => {
		if (titleRef.current) {
			const element = titleRef.current;
			const isTruncated = element.scrollHeight > element.clientHeight;
			setIsTitleTruncated(isTruncated);
		}
	}, [title, isHovered]);

	useEffect(() => {
		if (authorRef.current) {
			const element = authorRef.current;
			const isTruncated = element.scrollHeight > element.clientHeight;
			setIsAuthorTruncated(isTruncated);
		}
	}, [author, isHovered]);

	const shouldShowTooltip = isTitleTruncated || isAuthorTruncated;
	const tooltipContent = (
		<>
			<div>{displayTitle}</div>
			<div>
				{displayAuthor} • {audioType}
			</div>
		</>
	);

	const playlistItem = (
		<Box
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsHovered(true)}
			onBlur={() => setIsHovered(false)}
			xcss={cx(styles.gridContainer)}
		>
			{/* Main pressable button that spans the full grid */}
			<Pressable onClick={handleClick} xcss={styles.container}>
				<Flex gap="space.150" alignItems="center">
					<Box xcss={styles.iconWrapper}>
						<PlaylistItemIcon
							emojiId={data.emojiId}
							contentType={data.contentType}
							isLive={data.isLive}
						/>
					</Box>

					<Stack xcss={cx(styles.content, shouldShowMenuTrigger && styles.contentWithMenu)}>
						<Box xcss={styles.title}>
							<Text maxLines={1} ref={titleRef}>
								<Heading as="h5" size="xsmall">
									{displayTitle}
								</Heading>
							</Text>
						</Box>

						<Flex xcss={styles.subtitle}>
							<Box xcss={styles.authorName}>
								<Text size="small" color="color.text.subtle" maxLines={1} ref={authorRef}>
									{displayAuthor}
								</Text>
							</Box>
							<Box xcss={styles.separator}>
								<Text size="small" color="color.text.subtle">
									•
								</Text>
							</Box>
							<Box xcss={styles.type}>
								<Text size="small" color="color.text.subtle">
									{audioType}
								</Text>
							</Box>
						</Flex>
					</Stack>
				</Flex>
			</Pressable>

			<Box xcss={styles.contextualMenuOverlay}>
				<Box
					xcss={cx(
						styles.contextualMenuButton,
						shouldShowMenuTrigger && styles.contextualMenuButtonVisible,
					)}
				>
					<PlaylistItemContextualMenu
						onMenuOpenChange={setIsMenuOpen}
						isFirstItem={isFirstItem}
						isLastItem={isLastItem}
						data={data}
						currentIndex={currentIndex}
						allItems={allItems}
					/>
				</Box>
			</Box>
		</Box>
	);

	return shouldShowTooltip ? (
		<Tooltip content={tooltipContent}>{playlistItem}</Tooltip>
	) : (
		playlistItem
	);
};
